/**
 * The audit log of account security events: e-mail sign-up and sign-in addresses asked for, confirmed or changed,
 * password resets asked for and done, and the e-mails sent for them (services/email.ts), push devices (services/push.ts)
 * and Play Integrity verdicts (services/integrity.ts). Each event becomes
 *  - one document of the collection `account_audit` (id = zero-padded time + sequence + random tail: ids sort by time),
 *    kept by count and age (config.accountAudit: 90 days by default; the oldest go first);
 *  - one log line 'account audit' with the same facts.
 * An entry never holds an address, a token, a password or a push token: an address appears only as its tag (a keyed
 * hash, `addressTag`), which lets the operator see that two events concern the same address without storing it here.
 * The entries of a player go with its account (account deletion: `deletionOps`).
 */
import type { JsonObject } from '../../../shared/json';
import type { PlayerId } from '../../../shared/protocol/model';
import { randomId } from '../../../shared/random';
import { type StorageBatchOp, deleteOp, putOp } from '../storage';
import { C, type ServiceDeps } from './deps';

export interface AccountAuditEntry {
  auditId: string;
  at: number;
  /** 'email_signup', 'email_change_requested', 'email_verified', 'email_sent', 'password_reset_requested', ... */
  event: string;
  playerId: PlayerId | null;
  ok: boolean;
  details: JsonObject;
}

const PRUNE_EVERY = 500;
const PRUNE_MIN_INTERVAL_MS = 10 * 60_000;
const UNATTRIBUTED_LOG_INTERVAL_MS = 60_000;

export class AccountAudit {
  private seq = 0;
  private sincePrune = 0;
  private lastPruneAt = 0;
  private pruning: Promise<void> | null = null;
  private readonly unattributed = new Map<string, { count: number; loggedAt: number }>();

  constructor(
    private readonly d: ServiceDeps,
    /** Keyed hash of an address (AuthService.digest under a label): the same address always gets the same tag. */
    private readonly tagOf: (text: string) => string,
  ) {}

  /** The tag of an address key (first 16 hex characters of its keyed hash). */
  addressTag(key: string): string {
    return this.tagOf(`audit-address:${key}`).slice(0, 16);
  }

  /** Records an event. A storage failure is logged, never thrown (the event itself already happened). */
  async record(e: { event: string; playerId: PlayerId | null; ok: boolean; details?: JsonObject }): Promise<AccountAuditEntry> {
    const at = this.d.clock.now();
    const auditId = `${String(Math.max(0, Math.floor(at))).padStart(15, '0')}-${String(this.seq++ % 1_000_000).padStart(6, '0')}-${randomId(this.d.rng, '', 5)}`;
    const entry: AccountAuditEntry = { auditId, at, event: e.event, playerId: e.playerId, ok: e.ok, details: e.details ?? {} };
    try {
      await this.d.storage.batch([putOp(C.accountAudit, auditId, entry)]);
    } catch (err) {
      this.d.logger.error('account audit entry not stored', { event: entry.event, err });
    }
    const fields = { event: entry.event, playerId: entry.playerId, ok: entry.ok, ...entry.details };
    if (entry.ok) this.d.logger.info('account audit', fields);
    else this.d.logger.warn('account audit', fields);
    this.maybePrune();
    return entry;
  }

  /**
   * A refusal that names no account (a link token nobody was given: auth.emailVerify, auth.passwordReset): anybody can
   * send these at the request rate, so they are not stored (a document each would cost a durable write and push the
   * real entries out of the count limit) but counted per event and logged at most once a minute with the count since
   * the last line ('account audit: unattributed refusals').
   */
  noteUnattributed(event: string): void {
    const now = this.d.clock.now();
    const c = this.unattributed.get(event) ?? { count: 0, loggedAt: -Infinity };
    c.count++;
    if (now - c.loggedAt >= UNATTRIBUTED_LOG_INTERVAL_MS) {
      this.d.logger.warn('account audit: unattributed refusals', { event, count: c.count, why: 'unknown' });
      c.count = 0;
      c.loggedAt = now;
    }
    this.unattributed.set(event, c);
  }

  /** Entries, newest first (optionally of one player), at most `limit`. */
  async list(opts: { playerId?: PlayerId; limit?: number } = {}): Promise<AccountAuditEntry[]> {
    const all = await this.d.storage.list<AccountAuditEntry>(C.accountAudit);
    return all
      .filter((e) => opts.playerId === undefined || e.playerId === opts.playerId)
      .sort((a, b) => (a.auditId < b.auditId ? 1 : a.auditId > b.auditId ? -1 : 0))
      .slice(0, Math.max(1, Math.min(opts.limit ?? 100, 10_000)));
  }

  /** The player's entries (account deletion). */
  async deletionOps(playerId: PlayerId): Promise<StorageBatchOp[]> {
    const all = await this.d.storage.list<AccountAuditEntry>(C.accountAudit);
    return all.filter((e) => e?.playerId === playerId).map((e) => deleteOp(C.accountAudit, e.auditId));
  }

  private maybePrune(): void {
    if (++this.sincePrune < PRUNE_EVERY || this.pruning) return;
    const now = this.d.clock.now();
    if (now - this.lastPruneAt < PRUNE_MIN_INTERVAL_MS) return;
    this.sincePrune = 0;
    this.lastPruneAt = now;
    this.pruning = this.prune(now)
      .then(
        () => undefined,
        (err) => this.d.logger.error('account audit prune failed', { err }),
      )
      .finally(() => {
        this.pruning = null;
      });
  }

  /** Deletes the entries beyond the count and age limits (oldest first). Returns how many went. */
  async prune(now = this.d.clock.now()): Promise<number> {
    const { maxEntries, maxAgeMs } = this.d.config.accountAudit;
    const all = (await this.d.storage.list<AccountAuditEntry>(C.accountAudit)).sort((a, b) => (a.auditId < b.auditId ? -1 : a.auditId > b.auditId ? 1 : 0));
    const drop = new Set<string>();
    for (const e of all) if (now - e.at > maxAgeMs) drop.add(e.auditId);
    const left = all.filter((e) => !drop.has(e.auditId));
    for (let i = 0; i < left.length - maxEntries; i++) drop.add(left[i].auditId);
    const ids = [...drop];
    for (let i = 0; i < ids.length; i += 500) await this.d.storage.batch(ids.slice(i, i + 500).map((id) => deleteOp(C.accountAudit, id)));
    return ids.length;
  }
}
